import React, { createContext, useCallback, useEffect, useRef, useState } from "react";

export const ServerContext = createContext(null);

const parseRconPlayers = (rconResponse) => {
  if (!rconResponse) return [];
  if (rconResponse === "No Players Connected") return [];

  const lines = rconResponse.split(/\r?\n/);

  return lines
    .map((line) => {
      const beforeComma = line.split(",")[0] ?? "";
      return beforeComma.replace(/^\d+\.\s*/, "").trim();
    })
    .filter(Boolean);
};

const areServerInfosEqual = (a, b) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = a[i];
    const bi = b[i];
    if (!ai || !bi) return false;

    if (
      ai.status !== bi.status ||
      ai.server_name !== bi.server_name ||
      ai.map !== bi.map ||
      ai.version !== bi.version ||
      ai.player_current !== bi.player_current ||
      ai.player_max !== bi.player_max
    ) {
      return false;
    }
  }
  return true;
};

const arePlayersEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.every((arr, i) =>
    b[i]?.length === arr.length && arr.every((name, j) => name === b[i][j])
  );
};

export const ServerContextProvider = ({ children }) => {
  const [servers, setServers] = useState([]);
  const [serverInfos, setServerInfos] = useState([]);
  const [serverPlayers, setServerPlayers] = useState([]);

  const [isLoadingServers, setIsLoadingServers] = useState(true);
  const [isLoadingRcon, setIsLoadingRcon] = useState(false);

  const serverInfosRef = useRef(serverInfos);
  const serverPlayersRef = useRef(serverPlayers);

  useEffect(() => {
    serverInfosRef.current = serverInfos;
  }, [serverInfos]);

  useEffect(() => {
    serverPlayersRef.current = serverPlayers;
  }, [serverPlayers]);

  useEffect(() => {
    let isMounted = true;

    const loadServers = async () => {
      try {
        setIsLoadingServers(true);

        const res = await fetch("/servers.json");
        if (!res.ok) throw new Error("servers.json konnte nicht geladen werden");

        const json = await res.json();
        if (!isMounted) return;

        setServers(Array.isArray(json) ? json : []);
      } catch (e) {
        console.error("Fehler beim Laden von servers.json:", e);
      } finally {
        if (isMounted) setIsLoadingServers(false);
      }
    };

    loadServers();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchRconAll = useCallback(async () => {
    if (!servers.length) return;

    try {
      setIsLoadingRcon(true);

      const response = await fetch(`https://www.nexark.de/proxy.php?mode=rcon_all`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command: "listplayers" }),
      });

      if (!response.ok) throw new Error("Fehler beim Abrufen der RCON Daten");
      const data = await response.json();

			console.log(data)

      if (!data?.results || !Array.isArray(data.results)) return;

      const resultsByServerName = new Map(data.results.map((r) => [r.server, r]));

      const infos = servers.map((srv) => {
        const rcon = resultsByServerName.get(srv.server);

        return {
          status: rcon?.status || "unbekannt",
          server_name: rcon?.server || srv.name || "Syncing..",
          map: rcon?.map || "Syncing..",
					isRotation: rcon?.isRotation ?? false,
          version: rcon?.ark_version || "Syncing..",
          player_current: parseRconPlayers(rcon?.rcon_response).length,
          player_max: rcon?.max_players ?? 0,
        };
      });

      const playersList = servers.map((srv) => {
        const rcon = resultsByServerName.get(srv.server);
        return parseRconPlayers(rcon?.rcon_response);
      });

      if (!areServerInfosEqual(infos, serverInfosRef.current)) {
        setServerInfos(infos);
      }

      if (!arePlayersEqual(playersList, serverPlayersRef.current)) {
        setServerPlayers(playersList);
      }
    } catch (e) {
      console.error("Fehler beim Laden der Serverinfos:", e);
    } finally {
      setIsLoadingRcon(false);
    }
  }, [servers]);

  useEffect(() => {
    if (!servers.length) return;

    let intervalId;
    let isMounted = true;

    const wrapper = async () => {
      if (!isMounted) return;
      await fetchRconAll();
    };

    wrapper();
    intervalId = setInterval(wrapper, 25000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [servers, fetchRconAll]);

  const value = {
    servers,
    serverInfos,
    serverPlayers,

    isLoadingServers,
    isLoadingRcon,

    refresh: fetchRconAll,
  };

  return (
		<ServerContext.Provider value={value}>
			{children}
		</ServerContext.Provider>
	);
};
